/**
 * Latest Blog Comparative Section JavaScript
 * Handles animations for the mini scorecards in each article card
 * Supports multiple instances on the same page
 */

class LatestBlogComparative {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = this.section.getAttribute('data-section-id') || this.section.id.replace('latest-blog-comparative-', '');
    this.grid = this.section.querySelector('.latest-blog-comparative__grid');
    this.cards = this.section.querySelectorAll('.latest-blog-comparative__card');
    this.observers = [];
    this.init();
  }

  init() {
    this.handleDisplayMode();
    this.setupCardObservers();
  }

  handleDisplayMode() {
    if (!this.grid) return;

    const displayMode = this.grid.getAttribute('data-display-mode');
    const showCount = parseInt(this.grid.getAttribute('data-show-count'), 10) || 3;

    if (displayMode === 'random') {
      // Shuffle cards array using Fisher-Yates algorithm
      const cardsArray = Array.from(this.cards);
      for (let i = cardsArray.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [cardsArray[i], cardsArray[j]] = [cardsArray[j], cardsArray[i]];
      }

      // Show only the required number of random cards
      cardsArray.forEach((card, index) => {
        if (index < showCount) {
          card.classList.remove('latest-blog-comparative__card--hidden');
        } else {
          card.classList.add('latest-blog-comparative__card--hidden');
        }
      });

      // Update this.cards to only include visible ones for animation
      this.cards = this.section.querySelectorAll('.latest-blog-comparative__card:not(.latest-blog-comparative__card--hidden)');
    }
  }

  setupCardObservers() {
    const options = {
      root: null,
      rootMargin: '0px 0px -50px 0px',
      threshold: 0.2
    };

    this.cards.forEach((card) => {
      const progressBars = card.querySelectorAll('.latest-blog-comparative__progress-fill');

      // Initially pause all animations in this card
      progressBars.forEach(bar => {
        bar.style.animationPlayState = 'paused';
      });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animateCardProgressBars(card, progressBars);
            observer.unobserve(entry.target);
          }
        });
      }, options);

      observer.observe(card);
      this.observers.push(observer);
    });
  }

  animateCardProgressBars(card, progressBars) {
    // Check if this card has already been animated
    if (card.hasAttribute('data-animated')) {
      return;
    }

    // Mark card as animated
    card.setAttribute('data-animated', 'true');

    // Start animations with staggered timing
    progressBars.forEach((bar, index) => {
      setTimeout(() => {
        bar.style.animationPlayState = 'running';
      }, index * 100);
    });
  }

  destroy() {
    this.observers.forEach(observer => {
      observer.disconnect();
    });
    this.observers = [];

    // Clean up animation state
    this.cards.forEach(card => {
      card.removeAttribute('data-animated');
    });
  }
}

// Store instances to manage them properly
const latestBlogComparativeInstances = new Map();

// Function to initialize all latest-blog-comparative sections
function initializeLatestBlogComparativeSections() {
  const sections = document.querySelectorAll('[id^="latest-blog-comparative-"]');

  sections.forEach(section => {
    const sectionId = section.id;
    if (sectionId && !latestBlogComparativeInstances.has(sectionId)) {
      const instance = new LatestBlogComparative(section);
      latestBlogComparativeInstances.set(sectionId, instance);
    }
  });
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', initializeLatestBlogComparativeSections);

// Also initialize after a small delay to catch any dynamically loaded sections
setTimeout(initializeLatestBlogComparativeSections, 100);

// Handle Shopify section reloads (theme editor)
document.addEventListener('shopify:section:load', function(event) {
  if (event.target.id && event.target.id.startsWith('latest-blog-comparative-')) {
    const sectionId = event.target.id;

    // Destroy existing instance if it exists
    if (latestBlogComparativeInstances.has(sectionId)) {
      latestBlogComparativeInstances.get(sectionId).destroy();
      latestBlogComparativeInstances.delete(sectionId);
    }

    // Create new instance
    const instance = new LatestBlogComparative(event.target);
    latestBlogComparativeInstances.set(sectionId, instance);
  }
});

// Handle section unload
document.addEventListener('shopify:section:unload', function(event) {
  if (event.target.id && event.target.id.startsWith('latest-blog-comparative-')) {
    const sectionId = event.target.id;
    if (latestBlogComparativeInstances.has(sectionId)) {
      latestBlogComparativeInstances.get(sectionId).destroy();
      latestBlogComparativeInstances.delete(sectionId);
    }
  }
});
